import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import { LinkIcon } from '@utils/icons';
import type { ProviderName } from '@interfaces/IAuth';
import styles from './styles.module.css';

const PROVIDER_LABEL: Record<ProviderName, string> = { trusstor: 'Trusstor', genba: 'Genba' };

// Render order: Genba first (the re-auth-prone one), then Trusstor. Both can be
// lapsed at once, so we show a control for EACH affected provider rather than
// only the first — otherwise a second disconnected provider stays hidden and
// un-reconnectable behind the first.
const PROVIDER_ORDER = ['genba', 'trusstor'] as const satisfies readonly ProviderName[];

/**
 * TopBar controls prompting the user to (re)connect any upstream provider whose
 * session has lapsed. Driven by the global `providerStatus` flag (set by the
 * TopBar's `/api/auth/providers` poll — the single detection path); renders one
 * button per provider with an issue, nothing when all are healthy. Clicking
 * launches the existing connect flow (`/connect/:provider`), preserving the
 * current page as the redirect target.
 */
export function ProviderReconnectButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAppSelector((s) => s.providerStatus);

  const affected = PROVIDER_ORDER.filter((provider) => status[provider]);
  if (affected.length === 0) return null;

  return (
    <>
      {affected.map((provider) => {
        const issue = status[provider]!;
        const label =
          issue === 'not-connected'
            ? t('topbar.connectProvider', { provider: PROVIDER_LABEL[provider] })
            : t('topbar.reconnectProvider', { provider: PROVIDER_LABEL[provider] });
        return (
          <button
            key={provider}
            type="button"
            className={styles.reconnectBtn}
            data-testid={`provider-reconnect-${provider}`}
            data-provider={provider}
            data-variant={issue}
            aria-label={label}
            title={label}
            onClick={() =>
              navigate(`/connect/${provider}?redirect=${encodeURIComponent(location.pathname + location.search)}`)
            }
          >
            <LinkIcon size={14} />
            <span className={styles.reconnectLabel}>{label}</span>
          </button>
        );
      })}
    </>
  );
}
